import { Grid, Tooltip, Typography } from '@mui/material';
import { useEffect, useState, Fragment } from 'react';
import { IAccessControlSettings } from 'wisestep-api';
import { WsTypography, WsTypoVariants } from 'wisestep-base';

import PermissionControlChild from './PermissionControlChild';

export interface IAccessControlSettingsGrouped {
  [key: string]: IAccessControlSettings[];
}

export interface IPermissionGridProps {
  accountPermissions: IAccessControlSettings[];
  handleChange: (data: IAccessControlSettings[]) => void;
  expansionConfig: any;
}
type CategoriesTypes = 'VIEW' | 'MANAGE' | 'DELETE';
type CategoriesTypesMap = {
  [category in CategoriesTypes]: boolean;
};
const PermissionGridDerived = ({
  accountPermissions,
  handleChange,
  expansionConfig,
}: IPermissionGridProps) => {
  const borderStyle = '1px solid gray';

  const permissionTitleCellStyle = {
    borderRight: borderStyle,
  };

  const gridStyle = { border: borderStyle };
  const headerCellStyleWithWidth = {
    borderRight: borderStyle,
    borderBottom: borderStyle,
    height: '65px',
    display: 'flex',
    justifyContent: 'center',
  };
  const cellStyle = {
    borderRight: borderStyle,
    paddingTop: '4px',
    display: 'flex',
    justifyContent: 'center',
  };
  const gricColStyle = {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '1em',
  };

  const gridTitleColSpan = 2.5;
  const GRID_FULL_SPAN = 12;
  const [colWidth, setColWidth] = useState(
    (GRID_FULL_SPAN - gridTitleColSpan) / (accountPermissions?.length ?? 1)
  );
  const [categories] = useState<CategoriesTypes[]>(['VIEW', 'MANAGE', 'DELETE']);
  const [permissionMatrix, setPermissionMatrix] = useState(accountPermissions);

  const headerPermission = permissionMatrix?.reduce((cum, item) => {
    cum[item.roleKey] = { checked: true, indeterminate: false };
    return cum;
  }, {});

  const [headerPermissionMap, setHeaderPermissionMap] = useState(
    Object.keys(permissionMatrix[0].permissions).reduce((cum, item) => {
      cum[item] = JSON.parse(JSON.stringify(headerPermission));
      return cum;
    }, {})
  );

  const [expendedMap, setExpensionMap] = useState<CategoriesTypesMap>({
    VIEW: true,
    MANAGE: false,
    DELETE: false,
    ...expansionConfig,
  });

  const calcParentCheckBoxCheckedStatus = (accountPermissions?: IAccessControlSettings[]) => {
    (accountPermissions ?? permissionMatrix)?.forEach((col) => {
      categories.forEach((cat: string) => {
        const colPermission = col.permissions[cat];
        const unchecked = colPermission.some((rowVal) => rowVal.value == false);
        const checked = colPermission.some((rowVal) => rowVal.value == true);
        headerPermissionMap[cat][col.roleKey] = {
          checked,
          indeterminate: checked && unchecked,
        };
      });
      setHeaderPermissionMap({ ...headerPermissionMap });
    });
  };

  useEffect(() => {
    setPermissionMatrix(accountPermissions);
    const categoriesInner = ['VIEW', 'MANAGE', 'DELETE'];
    const headerPermission = accountPermissions?.reduce((cum, item) => {
      cum[item.roleKey] = { checked: true, indeterminate: false };
      return cum;
    }, {});

    const headerPermissionMapInner = Object.keys(accountPermissions[0].permissions).reduce(
      (cum, item) => {
        cum[item] = JSON.parse(JSON.stringify(headerPermission));
        return cum;
      },
      {}
    );
    setColWidth((GRID_FULL_SPAN - gridTitleColSpan) / (accountPermissions?.length ?? 0));
    accountPermissions?.forEach((col) => {
      categoriesInner.forEach((cat: string) => {
        const colPermission = col.permissions[cat];
        const unchecked = colPermission.some((rowVal) => rowVal.value == false);
        const checked = colPermission.some((rowVal) => rowVal.value == true);
        headerPermissionMapInner[cat][col.roleKey] = {
          checked,
          indeterminate: checked && unchecked,
        };
      });

      setHeaderPermissionMap(headerPermissionMapInner);
    });
  }, [accountPermissions]);

  const handleChangeEventChildRow = (colValue: any, currentValue) => {
    colValue.value = currentValue?.target.checked;
    if (permissionMatrix) handleChange(permissionMatrix);
    calcParentCheckBoxCheckedStatus();
  };

  useEffect(() => {
    setExpensionMap({
      VIEW: false,
      MANAGE: false,
      DELETE: false,
      ...expansionConfig,
    });
  }, [expansionConfig]);

  const limitOverflow: { [prop: string]: string } = {
    width: '100%',
    whiteSpace: 'normal',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'inline-block',
    alignItems: 'center',
  };
  return (
    <Grid container spacing={2} style={gridStyle}>
      <Grid item xs={gridTitleColSpan} style={headerCellStyleWithWidth}>
        <WsTypography variant={WsTypoVariants.TITLE} bold={true}>
          Permissions
        </WsTypography>
      </Grid>
      {permissionMatrix.map((item, colPermIndex) => (
        <Grid key={colPermIndex} item xs={colWidth} style={headerCellStyleWithWidth}>
          <Tooltip title={item.role}>
            <Typography variant={WsTypoVariants.TITLE} style={limitOverflow}>
              {item.role}
            </Typography>
          </Tooltip>
        </Grid>
      ))}
      {categories.map((category, permIndex) => {
        return (
          <Fragment key={permIndex}>
            <Fragment>
              {expendedMap[category] &&
                permissionMatrix?.length &&
                permissionMatrix[0]?.permissions[category].map((matRow, rowIndex) => {
                  return (
                    <Fragment key={rowIndex}>
                      <Grid
                        xs={gridTitleColSpan}
                        style={{
                          ...permissionTitleCellStyle,
                          ...gricColStyle,
                        }}
                      >
                        {matRow.action}
                      </Grid>
                      {permissionMatrix.map((matCol, colIndex) => {
                        const data = permissionMatrix[colIndex]?.permissions[category][rowIndex];
                        return (
                          <Fragment key={colIndex}>
                            <Grid item xs={colWidth} style={{ ...cellStyle, ...gricColStyle }}>
                              <PermissionControlChild
                                permission={data}
                                handleChangeEventChildRow={handleChangeEventChildRow}
                                checked={data.value}
                                headerChecked={
                                  headerPermissionMap[category][matCol.roleKey].checked
                                }
                                headerIndeterminate={
                                  headerPermissionMap[category][matCol.roleKey].indeterminate
                                }
                              />
                            </Grid>
                          </Fragment>
                        );
                      })}
                    </Fragment>
                  );
                })}
            </Fragment>
          </Fragment>
        );
      })}
    </Grid>
  );
};

export default PermissionGridDerived;
